import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Masonry column layout — CSS columns keep it simple and responsive.
// Max 9 images (backend enforces).
const ImpressionenGallery = () => {
  const [images, setImages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API}/gallery`)
      .then((r) => { if (!cancel) { setImages(Array.isArray(r.data) ? r.data : []); setLoaded(true); } })
      .catch(() => { if (!cancel) setLoaded(true); });
    return () => { cancel = true; };
  }, []);

  const close = useCallback(() => setOpenIdx(null), []);
  const next = useCallback(() => setOpenIdx((i) => (i === null ? null : (i + 1) % images.length)), [images.length]);
  const prev = useCallback(() => setOpenIdx((i) => (i === null ? null : (i - 1 + images.length) % images.length)), [images.length]);

  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [openIdx, close, next, prev]);

  if (!loaded || images.length === 0) return null;

  return (
    <section id="impressionen" className="relative py-20 md:py-28" data-testid="impressionen-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 md:mb-14 max-w-2xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#E8E5D0] mb-3">
            Galerie
          </div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter leading-[0.95] text-[#F3F1E8]">
            Impressionen.
          </h2>
          <p className="mt-5 text-[#C9C6B4] leading-relaxed">
            Ausschnitte aus Training, Coaching und echten Momenten – ehrliche
            Bilder statt inszenierter Bühne.
          </p>
        </div>

        {/* Masonry via CSS columns */}
        <div
          className="columns-1 sm:columns-2 lg:columns-3 gap-4 [column-fill:_balance]"
          data-testid="impressionen-masonry"
        >
          {images.map((img, i) => (
            <motion.button
              type="button"
              key={img.id}
              onClick={() => setOpenIdx(i)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
              className="mb-4 block w-full break-inside-avoid rounded-2xl overflow-hidden border border-white/12 bg-black/20 group relative"
              data-testid={`impressionen-item-${img.id}`}
              aria-label={img.alt || img.title || "Bild vergrößern"}
            >
              <img
                src={`${BACKEND_URL}${img.url}`}
                alt={img.alt || img.title || "Impression"}
                loading="lazy"
                className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              {img.title && (
                <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="font-bold text-white text-sm sm:text-base">{img.title}</div>
                  <div className="text-[11px] text-[#E8E5D0] uppercase tracking-wider mt-0.5">
                    Tippen zum Vergrößern
                  </div>
                </div>
              )}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {openIdx !== null && images[openIdx] && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={close}
            data-testid="lightbox"
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); close(); }}
              className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center"
              aria-label="Schließen"
              data-testid="lightbox-close"
            >
              <X size={18} />
            </button>
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); prev(); }}
                  className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center"
                  aria-label="Vorheriges"
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); next(); }}
                  className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center"
                  aria-label="Nächstes"
                  data-testid="lightbox-next"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}
            <motion.figure
              key={images[openIdx].id}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="max-w-6xl w-full max-h-[85vh] flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={`${BACKEND_URL}${images[openIdx].url}`}
                alt={images[openIdx].alt || images[openIdx].title || "Impression"}
                className="max-h-[75vh] max-w-full w-auto h-auto object-contain rounded-xl shadow-2xl"
              />
              {(images[openIdx].title || images[openIdx].alt) && (
                <figcaption className="mt-4 text-center text-[#E8E5D0] text-sm">
                  {images[openIdx].title}
                  {images[openIdx].title && images[openIdx].alt ? " · " : ""}
                  <span className="text-[#A8A58F]">{images[openIdx].alt}</span>
                </figcaption>
              )}
              <div className="mt-2 text-[11px] uppercase tracking-wider text-[#A8A58F]">
                {openIdx + 1} / {images.length}
              </div>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ImpressionenGallery;
